import type { ApolloCache } from "@apollo/client";
import { insertIntoConnection, removeFromConnection } from "#app/lib/data/connection-cache-utils";
import type { UpdateIssueTypeInput } from "#app/lib/data";

const OPTIMISTIC_LAYOUT_KEYS = [
  "propertyOrder",
  "sectionOrder",
  "hiddenSections",
  "requiredProperties",
] as const;

export function addIssueTypeToCache(cache: ApolloCache, issueType: any) {
  insertIntoConnection(cache, "issueTypes", "IssueTypeEdge", issueType);
}

export function removeIssueTypeFromCache(cache: ApolloCache, issueTypeId: string) {
  removeFromConnection(cache, "issueTypes", { __typename: "IssueType", issueTypeId });
}

// Writes the layout lists an update carries straight into the cached type and
// answers the undo, or null when the input carries none of them.
export function writeIssueTypeLayout(
  cache: ApolloCache,
  issueTypeId: string,
  input: UpdateIssueTypeInput,
): (() => void) | null {
  const cacheId = cache.identify({ __typename: "IssueType", issueTypeId });
  if (!cacheId) return null;
  const next: Record<string, unknown> = {};
  for (const key of OPTIMISTIC_LAYOUT_KEYS) {
    if (input[key] !== undefined) next[key] = input[key];
  }
  if (Object.keys(next).length === 0) return null;
  const previous: Record<string, unknown> = {};
  const write = (values: Record<string, unknown>, keep?: Record<string, unknown>) =>
    cache.modify({
      id: cacheId,
      fields: Object.fromEntries(
        Object.entries(values).map(([key, value]) => [
          key,
          (existing: unknown) => {
            if (keep) keep[key] = existing;
            return value;
          },
        ]),
      ),
    });
  write(next, previous);
  return () => write(previous);
}
